import { PARTICIPANT_SIDEBAR_ITEMS } from '../../../utils/participantSidebarPolicies';
import {
  POLICY_BACKED_ACCESS_BILLING_ADMIN,
  POLICY_BACKED_ACCESS_BILLING,
  POLICY_BACKED_ACCESS_DOC_LIBRARY_ADMIN,
  POLICY_BACKED_ACCESS_DOC_LIBRARY_EXTERNAL,
  POLICY_BACKED_ACCESS_DOC_LIBRARY_INTERNAL,
  POLICY_BACKED_ACCESS_INREP,
  POLICY_BACKED_ACCESS_LAI_JAIL,
  POLICY_BACKED_ACCESS_LAI_JAIL_WRITE,
  POLICY_BACKED_ACCESS_MEDCALL_POWER_USER,
  POLICY_BACKED_ACCESS_MEDCALL_STANDARD,
  POLICY_BACKED_ACCESS_NOTIFICATION_LOG,
  POLICY_BACKED_ACCESS_NOTIFICATIONS_VIA_OUTLOOK,
  POLICY_BACKED_ACCESS_PROGRESS_REPORT,
  POLICY_BACKED_ACCESS_REFERRAL_INTAKE,
  POLICY_BACKED_ACCESS_USER_ACCOUNT_REQUESTS
} from '../../../utils/userPermissionAccessPolicies';

export type UserManagerPermissionTemplate = {
  id: string;
  label: string;
  permissions: string[];
};

export type UserManagerPermissionTemplateConfig = {
  templates: UserManagerPermissionTemplate[];
};

export type UserManagerPermissionTemplateOption = {
  value: string;
  label: string;
  category: string;
};

const normalizeKey = (value: unknown) => `${value || ''}`.trim().toLowerCase();

const programOptions: UserManagerPermissionTemplateOption[] = [
  { value: 'program:mist', label: 'MIST', category: 'Programs' },
  { value: 'program:fist', label: 'FIST', category: 'Programs' },
  { value: 'program:odrh', label: 'ODR Housing', category: 'Programs' },
  { value: 'program:prgwmn', label: 'Pregnant Women', category: 'Programs' },
  { value: 'program:dsh', label: 'DSH', category: 'Programs' },
  { value: 'program:lead', label: 'LEAD', category: 'Programs' }
];

const participantOptions: UserManagerPermissionTemplateOption[] = PARTICIPANT_SIDEBAR_ITEMS.map((item) => ({
  value: `program:participant:${item.value}`,
  label: item.label,
  category: 'Participant Sections'
}));

const standardToolOptions: UserManagerPermissionTemplateOption[] = [
  { value: 'tools:reports:access', label: 'Reports', category: 'Tools' },
  { value: POLICY_BACKED_ACCESS_INREP, label: 'Incident Reports', category: 'Tools' },
  { value: POLICY_BACKED_ACCESS_PROGRESS_REPORT, label: 'Progress Reports', category: 'Tools' },
  { value: 'tools:efax:access', label: 'eFax', category: 'Tools' },
  { value: 'tools:dd2', label: 'DD2', category: 'Tools' },
  { value: 'tools:caseconference', label: 'Case Conferencing', category: 'Tools' },
  { value: 'tools:courtcalendar', label: 'Court Calendar', category: 'Tools' },
  { value: 'tools:housing', label: 'Housing Dashboard', category: 'Tools' }
];

const directToolOptions: UserManagerPermissionTemplateOption[] = [
  { value: POLICY_BACKED_ACCESS_USER_ACCOUNT_REQUESTS, label: 'User Account Requests', category: 'Tools' },
  { value: POLICY_BACKED_ACCESS_NOTIFICATION_LOG, label: 'Notification Log', category: 'Tools' },
  { value: POLICY_BACKED_ACCESS_NOTIFICATIONS_VIA_OUTLOOK, label: 'Notifications via Outlook', category: 'Tools' },
  { value: POLICY_BACKED_ACCESS_REFERRAL_INTAKE, label: 'Public Defenders List', category: 'Tools' },
  { value: POLICY_BACKED_ACCESS_DOC_LIBRARY_EXTERNAL, label: 'Doc Library External', category: 'Tools' },
  { value: POLICY_BACKED_ACCESS_DOC_LIBRARY_INTERNAL, label: 'Doc Library Internal', category: 'Tools' },
  { value: POLICY_BACKED_ACCESS_DOC_LIBRARY_ADMIN, label: 'Doc Library Admin', category: 'Tools' },
  { value: POLICY_BACKED_ACCESS_LAI_JAIL, label: 'LAI Jail', category: 'Tools' },
  { value: POLICY_BACKED_ACCESS_LAI_JAIL_WRITE, label: 'LAI Jail (write)', category: 'Tools' },
  { value: POLICY_BACKED_ACCESS_MEDCALL_STANDARD, label: 'MedCall Standard', category: 'Tools' },
  { value: POLICY_BACKED_ACCESS_MEDCALL_POWER_USER, label: 'MedCall Power User', category: 'Tools' },
  { value: POLICY_BACKED_ACCESS_BILLING, label: 'Billing', category: 'Tools' },
  { value: POLICY_BACKED_ACCESS_BILLING_ADMIN, label: 'Billing Admin', category: 'Tools' }
];

export const USER_MANAGER_PERMISSION_TEMPLATE_OPTIONS: UserManagerPermissionTemplateOption[] = [
  ...programOptions,
  ...participantOptions,
  ...standardToolOptions,
  ...directToolOptions
];

const allowedValueByKey = new Map(
  USER_MANAGER_PERMISSION_TEMPLATE_OPTIONS.map((option) => [normalizeKey(option.value), option.value])
);

const legacyValueMappings = new Map<string, string[]>([
  ['diversionaccess', ['program:mist']],
  ['fistaccess', ['program:fist']],
  ['fisteditparticipant', ['program:fist']],
  ['odrhousingaccess', ['program:odrh']],
  ['prgwmnaccess', ['program:prgwmn']],
  ['dshaccess', ['program:dsh']],
  ['leadaccess', ['program:lead']],
  ['inrepaccess', [POLICY_BACKED_ACCESS_INREP]],
  ['progressreportaccess', [POLICY_BACKED_ACCESS_PROGRESS_REPORT]],
  ['efaxaccess', ['tools:efax:access']],
  ['courtcalendaraccess', ['tools:courtcalendar']],
  ['hsngdshbrdaccess', ['tools:housing']],
  ['caseconferencingtool', ['tools:caseconference']],
  ['documentslibraryaccess', [POLICY_BACKED_ACCESS_DOC_LIBRARY_ADMIN]],
  ['laiaccess', [POLICY_BACKED_ACCESS_LAI_JAIL]],
  ['medcalstandardaccess', [POLICY_BACKED_ACCESS_MEDCALL_STANDARD]],
  ['medcalsuperaccess', [POLICY_BACKED_ACCESS_MEDCALL_POWER_USER]],
  ['ecmdashboardaccess', [POLICY_BACKED_ACCESS_BILLING]],
  ['ecmoutreachaccess', [POLICY_BACKED_ACCESS_BILLING]],
  ['legacyecmdashboardaccess', [POLICY_BACKED_ACCESS_BILLING]],
  ['providermedicaldemographicsaccess', [POLICY_BACKED_ACCESS_BILLING]],
  ['miftoolsaccess', [POLICY_BACKED_ACCESS_BILLING_ADMIN]],
  ['ecmdashboardsuperaccess', [POLICY_BACKED_ACCESS_BILLING_ADMIN]]
]);

export const USER_MANAGER_PERMISSION_TEMPLATE_MANAGED_VALUES = USER_MANAGER_PERMISSION_TEMPLATE_OPTIONS.map(
  (option) => option.value
);

export const DEFAULT_USER_MANAGER_PERMISSION_TEMPLATES: UserManagerPermissionTemplate[] = [
  {
    id: 'odr-staff',
    label: 'ODR Staff',
    permissions: [
      ...programOptions.map((item) => item.value),
      ...participantOptions.map((item) => item.value),
      POLICY_BACKED_ACCESS_DOC_LIBRARY_INTERNAL,
      POLICY_BACKED_ACCESS_MEDCALL_STANDARD,
      POLICY_BACKED_ACCESS_LAI_JAIL,
      'tools:reports:access',
      POLICY_BACKED_ACCESS_INREP,
      POLICY_BACKED_ACCESS_PROGRESS_REPORT,
      'tools:caseconference',
      'tools:housing'
    ]
  },
  {
    id: 'clinical',
    label: 'Clinical',
    permissions: [
      'program:participant:coordinatedcare',
      'program:participant:outreach',
      'program:participant:referencebrowser',
      'program:participant:documentscans',
      'program:participant:enrollment',
      'program:participant:medical',
      'program:participant:notes',
      'program:participant:notifications',
      'program:participant:scans',
      POLICY_BACKED_ACCESS_MEDCALL_STANDARD,
      'tools:reports:access',
      POLICY_BACKED_ACCESS_INREP,
      POLICY_BACKED_ACCESS_PROGRESS_REPORT,
      'tools:caseconference'
    ]
  },
  {
    id: 'nonclinical',
    label: 'Nonclinical',
    permissions: [
      ...programOptions.map((item) => item.value),
      'program:participant:missingitems',
      'program:participant:candidates',
      'program:participant:incidents',
      'program:participant:legal',
      'program:participant:disposition',
      'program:participant:programs',
      'tools:reports:access',
      POLICY_BACKED_ACCESS_INREP,
      POLICY_BACKED_ACCESS_PROGRESS_REPORT
    ]
  }
];

const resolveManagedValues = (value: unknown): string[] => {
  const key = normalizeKey(value);
  if (!key) {
    return [];
  }
  const allowedValue = allowedValueByKey.get(key);
  if (allowedValue) {
    return [allowedValue];
  }
  const participantValue = allowedValueByKey.get(`program:participant:${key}`);
  if (participantValue) {
    return [participantValue];
  }
  return legacyValueMappings.get(key) || [];
};

const normalizePermissionList = (values: unknown): string[] => {
  const nextValues: string[] = [];
  const seen = new Set<string>();

  (Array.isArray(values) ? values : []).forEach((value) => {
    resolveManagedValues(value).forEach((allowedValue) => {
      const key = normalizeKey(allowedValue);
      if (!allowedValue || seen.has(key)) {
        return;
      }

      seen.add(key);
      nextValues.push(allowedValue);
    });
  });

  return nextValues;
};

export const normalizeUserManagerPermissionTemplateConfig = (
  value: unknown
): UserManagerPermissionTemplateConfig => {
  const sourceTemplates = Array.isArray((value as any)?.templates)
    ? (value as any).templates
    : DEFAULT_USER_MANAGER_PERMISSION_TEMPLATES;
  const templates: UserManagerPermissionTemplate[] = [];
  const seenIds = new Set<string>();

  sourceTemplates.forEach((template: any, index: number) => {
    const label = `${template?.label || ''}`.trim();
    const id = `${template?.id || label || `template-${index + 1}`}`
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
    if (!id || !label || seenIds.has(id)) {
      return;
    }

    seenIds.add(id);
    templates.push({
      id,
      label,
      permissions: normalizePermissionList(template?.permissions)
    });
  });

  return { templates };
};
